import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ComponentFactoryResolver,
	Input,
	OnInit,
	ViewChild,
	OnDestroy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentFormsDirective } from '../../directives/component-forms.directive';
import { FilenamePipe } from '../../pipes/filename.pipe';
import { ComponentService, CreatorFrontApi } from '../../services/component.service';
import { OverlayService } from '../../services/overlay.service';
import { Subscription, forkJoin } from 'rxjs';
import {
	AddToCalendarComponent,
	CarouselComponent,
	CustomComponent,
	DefaultComponent,
	DeviceTargetingComponent,
	DynamicCTAComponent,
	EngagementTrackerComponent,
	FadeComponent,
	GeoTargetedImagesComponent,
	SegmentedImagesComponent,
	ImageSwapComponent,
	InstagramComponent,
	MapComponent,
	NewsTickerComponent,
	PersonalizedImageComponent,
	PopupComponent,
	LocationComponent,
	TimerComponent,
	TwitterComponent,
	VideoComponent,
	WeatherComponent,
	WebScrapeComponent,
	WhatsappComponent,
	ZoomComponent,
	RevealComponent,
	AssetGeneratorComponent,
	GalleryComponent,
	RuleEngineComponent,
	RecommendationComponent,
	CouponComponent,
	SurveyComponent,
	BarcodeComponent,
	TranslateComponent,
	ProgressBarComponent
} from '../component-forms/index';
import { NewApiHttpService, NewApiResponse } from '../../services/new-api-http.service';
import { BeeFreeService } from '../../services/beefree.service';
import { MaintenanceService } from '../../services/maintenance.service';

declare let gtag;
const componentRegistry = {
	timer: TimerComponent,
	'add-to-calendar': AddToCalendarComponent,
	zoom: ZoomComponent,
	fade: FadeComponent,
	'dynamic-cta': DynamicCTAComponent,
	reveal: RevealComponent,
	'asset-generator': AssetGeneratorComponent,
	gallery: GalleryComponent,
	'engagement-tracker': EngagementTrackerComponent,
	whatsapp: WhatsappComponent,
	weather: WeatherComponent,
	map: MapComponent,
	'location-images': LocationComponent,
	video: VideoComponent,
	'device-targeting': DeviceTargetingComponent,
	'web-scrape': WebScrapeComponent,
	twitter: TwitterComponent,
	instagram: InstagramComponent,
	'personalized-image': PersonalizedImageComponent,
	'pop-up': PopupComponent,
	carousel: CarouselComponent,
	'news-ticker': NewsTickerComponent,
	'image-swap': ImageSwapComponent,
	'segmented-images': SegmentedImagesComponent,
	'geo-targeted-images': GeoTargetedImagesComponent,
	custom: CustomComponent,
	'rule-engine': RuleEngineComponent,
	recommendation: RecommendationComponent,
	coupon: CouponComponent,
	survey: SurveyComponent,
	barcode: BarcodeComponent,
	translate: TranslateComponent,
	'progress-bar': ProgressBarComponent
};

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-component-forms-container',
	templateUrl: './component-forms-container.component.html',
	styleUrls: ['./component-forms-container.component.scss'],
	entryComponents: [
		DefaultComponent,
		TimerComponent,
		AddToCalendarComponent,
		ZoomComponent,
		FadeComponent,
		DynamicCTAComponent,
		EngagementTrackerComponent,
		WhatsappComponent,
		WeatherComponent,
		MapComponent,
		LocationComponent,
		VideoComponent,
		DeviceTargetingComponent,
		WebScrapeComponent,
		TwitterComponent,
		InstagramComponent,
		PersonalizedImageComponent,
		PopupComponent,
		CarouselComponent,
		NewsTickerComponent,
		ImageSwapComponent,
		GeoTargetedImagesComponent,
		SegmentedImagesComponent,
		CustomComponent,
		RevealComponent,
		AssetGeneratorComponent,
		GalleryComponent,
		RuleEngineComponent,
		RecommendationComponent,
		CouponComponent,
		SurveyComponent,
		BarcodeComponent,
		TranslateComponent,
		ProgressBarComponent
	],
	providers: [FilenamePipe]
})
export class ComponentFormsContainerComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(ComponentFormsDirective, { static: true }) componentForm: ComponentFormsDirective;
	componentType: string;
	templateNum: number;
	@Input() componentData: any;
	imgUrls: Array<string>;
	disabledSaveSubscription: Subscription;
	componentDataSubscription: Subscription;
	componentDataToSave;
	componentID;
	duplicateID;
	disableSave = false;
	snippet;
	formHeight: string;
	overlaySubscription: Subscription;
	refresh: Boolean = Boolean(false);
	refreshType;
	refreshText;
	urlGlobalRegExp = new RegExp(
		/((https?:\/\/(?:[^\s./?#]+\.)+[A-Za-z\d-]{2,}(?:(?:\/|\?|#)[^\s]*)?)|(fb:\/\/)|(instagram:\/\/)|(snapchat:\/\/)|(pinterest:\/\/)|(twitter:\/\/)|(linkedin:\/\/)|(vnd.youtube:\/\/))/
	);
	emailRegExp = new RegExp(/^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/);
	noSpecialCharacters = new RegExp(/^[a-zA-Z0-9_-]*$/);
	renamedComponents = {
		'reveal-coupon': 'reveal',
		'store-locator': 'location-images',
		'image-merge': 'segmented-images'
	};
	supportedComponentsSubscription: Subscription;
	supportedComponentsSubscriptionType: Subscription;
	joinedSubscription: Subscription;
	missingComponentData = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private _componentFactoryResolver: ComponentFactoryResolver,
		private component: ComponentService,
		private filenamePipe: FilenamePipe,
		private overlay: OverlayService,
		private newApiHttpService: NewApiHttpService,
		private cd: ChangeDetectorRef,
		private beeFree: BeeFreeService,
		private maintenance: MaintenanceService
	) {}

	ngOnInit() {
		this.formHeight = String(`${window.innerHeight - 170}px`);
		this.componentType = this.route.snapshot.params['componentType'];
		this.componentID = this.route.snapshot.params['componentID'];
		this.duplicateID = this.route.snapshot.params['duplicateID'];

		this.fixRenamedRoutes(this.componentType);

		this.disabledSaveSubscription = this.component.disabledSave.subscribe((value) => {
			this.disableSave = !!value;
			this.cd.detectChanges();
		});

		if (this.componentType !== undefined) {
			// If "new" component page
			this.templateNum = this.route.snapshot.params['templateNum'] ? this.route.snapshot.params['templateNum'] : 0;
			this.componentData = this.component.getComponentTemplate(this.componentType, this.templateNum);
			this.sendGAPageViewWithTitle('New', this.componentData.config.desc, null);
		} else if (this.componentID !== undefined) {
			// If "edit" component page
			this.setupComponent(this.componentID, 'Edit');
		} else if (this.duplicateID !== undefined) {
			// If "duplicate" component page
			this.setupComponent(this.duplicateID, 'Duplicate');
		}

		this.componentDataSubscription = this.component.componentData.subscribe((result) => {
			this.componentDataToSave = result;
			this.cd.detectChanges();
		});

		this.overlaySubscription = this.overlay.refreshData.subscribe((callback) => {
			this.refresh = !!callback.active;
			this.refreshText = callback.text;
			this.refreshType = callback.type;
			this.cd.markForCheck();
		});
	}

	ngAfterViewInit() {
		if (this.componentType) {
			this.loadComponent(this.componentType);
			this.supportedComponentsSubscription = this.component.getSupportedComponents().subscribe((response: NewApiResponse) => {
				if (response && response.status) {
					const supportedcomponentItem = response.data.find((data) => {
						return data.type === this.componentData.config.type;
					});
					if (supportedcomponentItem && supportedcomponentItem.snippet) {
						this.snippet = supportedcomponentItem.snippet;
						this.component.updatePreviewContent(this.snippet);
					}
				}
			});
		}
	}

	ngOnDestroy() {
		this.disabledSaveSubscription.unsubscribe();
		this.componentDataSubscription.unsubscribe();
		if (this.supportedComponentsSubscription) {
			this.supportedComponentsSubscription.unsubscribe();
		}
		if (this.supportedComponentsSubscriptionType) {
			this.supportedComponentsSubscriptionType.unsubscribe();
		}
		if (this.joinedSubscription) {
			this.joinedSubscription.unsubscribe();
		}
	}

	checkComponentTypeByNumberAndLoad(typeNumber) {
		this.supportedComponentsSubscriptionType = this.component.getSupportedComponents().subscribe((response: NewApiResponse) => {
			if (response && response.status) {
				const supportedcomponentItem = response.data.find((data) => {
					return data.type === typeNumber;
				});
				if (supportedcomponentItem && supportedcomponentItem.name) {
					this.componentType = supportedcomponentItem.name;
				}
				this.loadComponent(this.componentType);
			}
		});
	}

	setupComponent(id, type) {
		let suportedComponents$ = this.component.getSupportedComponents();
		let componentData$ = this.component.getComponentData(id);

		this.joinedSubscription = forkJoin([componentData$, suportedComponents$]).subscribe(
			(results) => {
				const componentDataResults = results[0];
				const suportedComponentsResults = results[1];

				this.componentData = componentDataResults.data;

				this.sendGAPageViewWithTitle(type, this.componentData.config.desc, id);

				if (type === 'Edit') {
					this.component.updatePreviewContent(componentDataResults.data.snippet);

					const supportedcomponentItem = suportedComponentsResults.data.find((data) => {
						return data.type === this.componentData.config.type;
					});

					if (supportedcomponentItem && supportedcomponentItem.name) {
						this.componentType = supportedcomponentItem.name;
						this.loadComponent(this.componentType);
					} else {
						this.checkComponentTypeByNumberAndLoad(this.componentData.config.type);
					}
				} else if (type === 'Duplicate') {
					this.componentData.old_id = this.componentData.id;

					let checkedName = this.component.clonename(this.componentData.name);

					checkedName.then((res) => {
						this.componentData.name = res['validName'];

						this.component.updatePreviewContent(componentDataResults.data.snippet);

						const supportedcomponentItem = suportedComponentsResults.data.find((data) => {
							return data.type === this.componentData.config.type;
						});

						if (supportedcomponentItem && supportedcomponentItem.name) {
							this.componentType = supportedcomponentItem.name;
							this.loadComponent(this.componentType);
						} else {
							this.checkComponentTypeByNumberAndLoad(this.componentData.config.type);
						}
					});
				}
			},
			(err) => {
				if (!this.componentData) {
					this.missingComponentData = true;
					this.overlay.updateOverlay(true, 'Something bad happened. This component does not exist in this account.', 'alert');
					this.cd.detectChanges();
				}
				this.maintenance.serverMaintenance(err);
			}
		);
	}

	loadComponent(widgetType) {
		this.overlay.updateRefresh(true, 'Updating...', 'refresh');

		let componentFactory;

		if (componentRegistry[widgetType] === undefined) {
			componentFactory = this._componentFactoryResolver.resolveComponentFactory(DefaultComponent);
			this.disableSave = true;
		} else {
			componentFactory = this._componentFactoryResolver.resolveComponentFactory(componentRegistry[widgetType]);
		}

		const viewContainerRef = this.componentForm.viewContainerRef;
		viewContainerRef.clear();

		const componentRef = viewContainerRef.createComponent(componentFactory);
		(<ComponentData>componentRef.instance).componentData = this.componentData;
		(<any>componentRef.instance).formHeight = this.formHeight;
		(<any>componentRef.instance).pageType = this.route.snapshot.parent.url;
		(<any>componentRef.instance).urlGlobalRegExp = this.urlGlobalRegExp;
		(<any>componentRef.instance).emailRegExp = this.emailRegExp;
		(<any>componentRef.instance).noSpecialCharacters = this.noSpecialCharacters;
		if (this.componentID == undefined) {
			(<any>componentRef.instance).componentID = -1;
		} else {
			(<any>componentRef.instance).componentID = this.componentID;
		}

		/**
		 * Initialize the componentData stream from local config list of components
		 * This is help to avoid unneccessary fires of broadcastPreview or renderWidget on init
		 */
		this.component.updateComponentData(this.componentData);
		this.component.updatePreviewData();

		setTimeout(() => {
			this.cd.markForCheck();
		}, 0);
	}

	renderWidget(event) {
		this.component.updateComponentData(this.componentData);
		this.component.updatePreviewData();
	}

	saveComponent(event) {
		this.overlay.updateOverlay(true, 'Generating Component...');
		this.component.dispatchSaveEvent();

		const headers = new Headers();
		headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
		const componentId = this.componentID ? this.componentID : -1;
		let data = {
			name: this.componentDataToSave.name || this.componentData.name,
			config: JSON.stringify(
				this.componentDataToSave && this.componentDataToSave.config && Object.keys(this.componentDataToSave.config).length
					? this.componentDataToSave.config
					: this.componentData.config
			)
		};

		if (componentId > -1) {
			data['widgetId'] = componentId;
		}

		this.newApiHttpService.newApiRequestPost('widget', data, 0).subscribe(
			(response: NewApiResponse) => {
				const respError =
					response && response.message
						? response.message
						: `Ugh, it looks like this component is not accessible.
                   Please contact dmsupport@optimove.com if you need help.`;

				if (response && response.status) {
					this.overlay.updateOverlay(false);
					this.router.navigate(['/component/' + response.data.id]);
				} else {
					this.overlay.updateRefresh(false);
					this.overlay.updateOverlay(true, respError, 'alert');
				}
			},
			(error) => {
				this.component.initSaveEvent();
				this.overlay.updateRefresh(false);
				this.overlay.updateOverlay(true, error, 'alert');
			},
			() => this.component.initSaveEvent()
		);
	}

	sendGAPageViewWithTitle(pageType, componentType, widgetId) {
		const befreeAddonText = this.beeFree.isBeeFreeAddon() ? ' - Beefree Addon' : '';
		const widgetIDText = widgetId ? ` - Widget ID: ${widgetId}` : '';
		const titleText = `DynamicMail Creator - ${pageType} component - Widget type: ${componentType}${widgetIDText}${befreeAddonText}`;
		gtag('event', 'page_view', {
			page_path: this.router.url,
			page_title: titleText
		});
	}

	fixRenamedRoutes(componentType) {
		if (this.renamedComponents[componentType]) {
			this.componentType = this.renamedComponents[componentType];
			this.router.navigateByUrl(this.router.url.replace(componentType, this.componentType));
		}
	}
}

export interface ComponentData {
	componentData: any;
}
